import { useMemo, useState } from 'react';
import {
  delta, derive, playCr, type BreakdownRow, type Bucket, type PlayListing, type PlayListingBucket, type TrackerDashboard,
} from '@/api/tracker-dashboard';
import { countryName, downloadCsv, flagOf, fmtDelta, fmtInt, fmtMoney, fmtPct, shortDay } from './format';
import { TOP_METRICS, topIntersection, type TopMetric } from './topIntersection';

/** How deep the "top by" filter looks. */
const TOP_N = 10;

export type DimKey = 'app' | 'country' | 'app_country' | 'campaign' | 'offer' | 'partner' | 'source';

const TABS: { key: DimKey; label: string; field: keyof TrackerDashboard }[] = [
  { key: 'app', label: 'Apps', field: 'by_app' },
  { key: 'country', label: 'Geo', field: 'by_country' },
  { key: 'app_country', label: 'App × Geo', field: 'app_country' },
  { key: 'campaign', label: 'Campaigns', field: 'by_campaign' },
  { key: 'offer', label: 'Offers', field: 'by_offer' },
  { key: 'partner', label: 'Advertisers', field: 'by_partner' },
  { key: 'source', label: 'Source', field: 'by_source' },
];

const OTHER_KEY = '__other__';

type TrackerKey = 'clicks' | 'uniq_clicks' | 'regs' | 'cr_reg' | 'deps' | 'cr_dep' | 'revenue' | 'epc';
type PlayKey = 'play_visitors' | 'play_installs' | 'play_cr';
type SortKey = 'label' | TrackerKey | PlayKey;

const NUMERIC: TrackerKey[] = [
  'clicks', 'uniq_clicks', 'regs', 'cr_reg', 'deps', 'cr_dep', 'revenue', 'epc',
];

/** Takes a bare Bucket so the same accessor reads a row and its prev half. */
function metricOf(b: Bucket, k: TrackerKey): number {
  const d = derive(b);
  switch (k) {
    case 'clicks':      return b.clicks;
    case 'uniq_clicks': return b.uniq_clicks;
    case 'regs':        return b.regs;
    case 'cr_reg':      return d.cr_reg;
    case 'deps':        return b.deps;
    case 'cr_dep':      return d.cr_dep;
    case 'revenue':     return d.revenue;
    case 'epc':         return d.epc;
  }
}

function fmtMetric(k: TrackerKey, v: number): string {
  if (k === 'revenue') return fmtMoney(v);
  if (k === 'epc') return `$${v.toFixed(3)}`;
  if (k === 'cr_reg' || k === 'cr_dep') return fmtPct(v);
  return fmtInt(v);
}

const COL_LABEL: Record<TrackerKey, string> = {
  clicks: 'Clicks', uniq_clicks: 'Unique', regs: 'Regs', cr_reg: 'CR reg',
  deps: 'Deps', cr_dep: 'CR dep', revenue: 'Revenue', epc: 'EPC',
};

// Google Play store-listing columns. Play reports by app and country only, so
// they exist on exactly the tabs keyed by those.
const PLAY_TABS: DimKey[] = ['app', 'country', 'app_country'];
const PLAY_COLS: { key: PlayKey; label: string; title: string }[] = [
  { key: 'play_visitors', label: 'Store visits', title: 'Google Play store listing visitors' },
  { key: 'play_installs', label: 'Installs', title: 'Google Play store listing acquisitions — installs from the listing' },
  { key: 'play_cr', label: 'Store CR', title: 'Listing visitors → installs' },
];

function playField(tab: DimKey): 'by_app' | 'by_country' | 'app_country' {
  return tab === 'app' ? 'by_app' : tab === 'country' ? 'by_country' : 'app_country';
}

function playValue(b: PlayListingBucket | undefined, k: PlayKey): number | null {
  if (!b) return null;
  if (k === 'play_visitors') return b.visitors;
  if (k === 'play_installs') return b.acquisitions;
  return playCr(b);
}

function isPlayKey(k: SortKey): k is PlayKey {
  return k === 'play_visitors' || k === 'play_installs' || k === 'play_cr';
}

const ZERO: Bucket = { clicks: 0, uniq_clicks: 0, regs: 0, deps: 0, payout_cents: 0 };

interface Props {
  data: TrackerDashboard;
  /** Play store-listing numbers for the same window; undefined while loading. */
  play?: PlayListing;
  tab: DimKey;
  onTab: (t: DimKey) => void;
  /** Clicking a row narrows the whole dashboard to it. */
  onDrill: (dim: DimKey, row: BreakdownRow) => void;
  activeKeys: Partial<Record<DimKey, string>>;
}

export function TrackerBreakdown({ data, play, tab, onTab, onDrill, activeKeys }: Props) {
  const [sort, setSort] = useState<SortKey>('clicks');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  // Geo tab: keep only geos in the top 10 by every ticked metric at once.
  const [topBy, setTopBy] = useState<TopMetric[]>([]);
  const [copied, setCopied] = useState(false);

  const showPlay = !!play && PLAY_TABS.includes(tab);
  const playMap = useMemo<Record<string, PlayListingBucket>>(
    () => (showPlay ? play![playField(tab)] : {}), [showPlay, play, tab]);
  const trackerRows = (data[TABS.find((t) => t.key === tab)!.field] as BreakdownRow[]) ?? [];
  // An app or geo with store traffic but no tracker clicks in the window is
  // still an answer to "how does the listing convert", so it gets a row. The
  // App × Geo matrix is capped server-side and stays tracker-only.
  const allRows = useMemo(() => {
    if (!showPlay || tab === 'app_country') return trackerRows;
    const have = new Set(trackerRows.map((r) => r.key));
    const extra: BreakdownRow[] = [];
    for (const key of Object.keys(playMap)) {
      if (have.has(key)) continue;
      const app = tab === 'app' ? play!.by_app[key] : undefined;
      extra.push({ key, label: app?.label ?? key, sub: app?.sub ?? null, ...ZERO, prev: ZERO });
    }
    return extra.length ? [...trackerRows, ...extra] : trackerRows;
  }, [trackerRows, playMap, showPlay, tab, play]);
  const topKeys = useMemo(
    () => (tab === 'country' ? topIntersection(allRows, topBy, TOP_N, [OTHER_KEY]) : null),
    [allRows, topBy, tab],
  );
  const rows = useMemo(() => (topKeys ? allRows.filter((r) => topKeys.has(r.key)) : allRows), [allRows, topKeys]);

  // A Play sort left over from another tab falls back to clicks.
  const effSort: SortKey = isPlayKey(sort) && !showPlay ? 'clicks' : sort;

  const sorted = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      if (effSort === 'label') return a.label.localeCompare(b.label);
      // No Play data sorts below a real zero.
      if (isPlayKey(effSort)) return (playValue(playMap[a.key], effSort) ?? -1) - (playValue(playMap[b.key], effSort) ?? -1);
      return metricOf(a, effSort) - metricOf(b, effSort);
    });
    return dir === 'asc' ? out : out.reverse();
  }, [rows, effSort, dir, playMap]);

  // The share bar tracks whatever column the table is sorted by, so the visual
  // ranking and the numeric ranking can never tell different stories.
  const barValue = (r: BreakdownRow): number => (isPlayKey(effSort)
    ? playValue(playMap[r.key], effSort) ?? 0
    : metricOf(r, effSort === 'label' ? 'clicks' : effSort));
  // The Δ column compares tracker periods; Play has no comparison window.
  const barKey: TrackerKey = effSort === 'label' || isPlayKey(effSort) ? 'clicks' : effSort;
  const barMax = Math.max(...sorted.map(barValue), 0) || 1;

  function toggleSort(k: SortKey) {
    if (sort === k) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(k); setDir(k === 'label' ? 'asc' : 'desc'); }
  }

  function exportCsv() {
    downloadCsv(
      `tracker-${tab}-${data.range.from}_${data.range.to}.csv`,
      ['name', 'detail', 'clicks', 'unique', 'regs', 'cr_reg_pct', 'deps', 'cr_dep_pct', 'revenue_usd', 'epc_usd',
        'prev_clicks', 'prev_regs', 'prev_deps', 'prev_revenue_usd',
        ...(showPlay ? ['store_visitors', 'store_installs', 'store_cr_pct'] : [])],
      sorted.map((r) => {
        const d = derive(r);
        const p = playMap[r.key];
        return [
          r.label, r.sub ?? '', r.clicks, r.uniq_clicks, r.regs, d.cr_reg.toFixed(2), r.deps,
          d.cr_dep.toFixed(2), d.revenue.toFixed(2), d.epc.toFixed(4),
          r.prev.clicks, r.prev.regs, r.prev.deps, (r.prev.payout_cents / 100).toFixed(2),
          ...(showPlay ? (p ? [p.visitors, p.acquisitions, playCr(p).toFixed(2)] : ['', '', '']) : []),
        ];
      }),
    );
  }

  /** The shown geos as "ID, ZA, AT" — for the campaign sheet, in the table's order. */
  async function copyGeos() {
    const codes = sorted.filter((r) => r.key !== OTHER_KEY).map((r) => r.key).join(', ');
    try {
      await navigator.clipboard.writeText(codes);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  }

  const arrow = (k: SortKey) => (effSort === k ? (dir === 'asc' ? ' ↑' : ' ↓') : '');
  const thNum = 'px-3 py-2 text-right text-[11px] font-semibold text-content-subtle uppercase tracking-wider cursor-pointer select-none hover:text-content-muted whitespace-nowrap';

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border">
        <div className="flex flex-wrap bg-surface-muted rounded-lg p-0.5 gap-0.5">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => onTab(t.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                tab === t.key ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-content-subtle ml-1">
          {topKeys ? `${rows.length} of ${allRows.length} rows` : `${rows.length} rows`} · click a row to filter
        </span>
        {tab === 'country' && (
          <div className="flex flex-wrap items-center gap-2 ml-2 text-xs text-content-muted">
            <span className="text-content-subtle">Top {TOP_N} by</span>
            {TOP_METRICS.map((m) => (
              <label key={m.key} className="inline-flex items-center gap-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={topBy.includes(m.key)}
                  onChange={(e) => setTopBy((prev) => e.target.checked ? [...prev, m.key] : prev.filter((x) => x !== m.key))}
                />
                {m.label}
              </label>
            ))}
            {topBy.length > 1 && <span className="text-content-subtle">(in all at once)</span>}
            <button
              onClick={copyGeos}
              disabled={sorted.length === 0}
              className="px-2 py-1 font-medium border border-border rounded-md hover:bg-surface-muted disabled:opacity-40"
              title="Copy the shown geo codes, in the table order"
            >
              {copied ? 'Copied' : 'Copy geos'}
            </button>
          </div>
        )}
        <button
          onClick={exportCsv}
          className="ml-auto px-2.5 py-1.5 text-xs font-medium text-content-muted border border-border rounded-lg hover:bg-surface-muted transition-colors"
        >
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-surface-muted/60">
            <tr>
              <th
                className="px-4 py-2 text-left text-[11px] font-semibold text-content-subtle uppercase tracking-wider cursor-pointer select-none hover:text-content-muted"
                onClick={() => toggleSort('label')}
              >
                Name{arrow('label')}
              </th>
              {NUMERIC.map((k) => (
                <th key={k} className={thNum} onClick={() => toggleSort(k)}>
                  {COL_LABEL[k]}{arrow(k)}
                </th>
              ))}
              <th className="px-3 py-2 text-right text-[11px] font-semibold text-content-subtle uppercase tracking-wider whitespace-nowrap">
                Δ {COL_LABEL[barKey]}
              </th>
              {showPlay && PLAY_COLS.map((col, i) => (
                <th
                  key={col.key}
                  title={col.title}
                  className={`${thNum} bg-success/5 ${i === 0 ? 'border-l border-border' : ''}`}
                  onClick={() => toggleSort(col.key)}
                >
                  {col.label}{arrow(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.length === 0 && (
              <tr><td colSpan={NUMERIC.length + 2 + (showPlay ? PLAY_COLS.length : 0)} className="px-4 py-10 text-center text-sm text-content-subtle">
                {topKeys ? `No geo is in the top ${TOP_N} by all of the ticked metrics` : 'No traffic in this period'}
              </td></tr>
            )}
            {sorted.map((r) => {
              const isOther = r.key === OTHER_KEY;
              const isActive = activeKeys[tab] === r.key;
              const change = delta(metricOf(r, barKey), metricOf(r.prev, barKey));
              return (
                <tr
                  key={r.key}
                  onClick={() => !isOther && onDrill(tab, r)}
                  className={`${isOther ? '' : 'cursor-pointer'} ${
                    isActive ? 'bg-primary/5' : 'hover:bg-surface-muted/50'
                  } transition-colors`}
                >
                  <td className="px-4 py-2 max-w-[22rem]">
                    <div className="flex items-center gap-2">
                      {tab === 'country' && <span className="text-base leading-none">{flagOf(r.key)}</span>}
                      <div className="min-w-0">
                        <div className="text-sm text-content truncate">
                          {tab === 'country' ? countryName(r.key) : r.label}
                        </div>
                        {r.sub && (
                          <div className="text-[11px] text-content-subtle truncate flex items-center gap-1">
                            {tab === 'app_country' && <span>{flagOf(r.sub)}</span>}
                            {tab === 'app_country' ? countryName(r.sub) : r.sub}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 h-1 w-full max-w-[16rem] bg-surface-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary/60 rounded-full"
                        style={{ width: `${Math.max(0, (barValue(r) / barMax) * 100)}%` }}
                      />
                    </div>
                  </td>
                  {NUMERIC.map((k) => (
                    <td key={k} className={`px-3 py-2 text-right text-sm font-mono whitespace-nowrap ${
                      k === effSort ? 'text-content font-semibold' : 'text-content-muted'
                    }`}>
                      {fmtMetric(k, metricOf(r, k))}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right text-xs font-mono whitespace-nowrap">
                    <span className={
                      change === null ? 'text-content-subtle'
                        : change > 0.05 ? 'text-success'
                        : change < -0.05 ? 'text-danger'
                        : 'text-content-subtle'
                    }>
                      {fmtDelta(change)}
                    </span>
                  </td>
                  {showPlay && PLAY_COLS.map((col, i) => {
                    const v = playValue(playMap[r.key], col.key);
                    return (
                      <td key={col.key} className={`px-3 py-2 text-right text-sm font-mono whitespace-nowrap bg-success/5 ${
                        i === 0 ? 'border-l border-border' : ''
                      } ${col.key === effSort ? 'text-content font-semibold' : 'text-content-muted'}`}>
                        {v === null ? <span className="text-content-subtle">—</span>
                          : col.key === 'play_cr' ? fmtPct(v) : fmtInt(v)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showPlay && (
        <div className="px-4 py-2.5 border-t border-border text-[11px] text-content-subtle leading-relaxed">
          <span className="font-semibold text-content-muted">Store columns</span> — Google Play Console store listing,
          {' '}{fmtInt(play!.totals.visitors)} visits → {fmtInt(play!.totals.acquisitions)} installs
          {play!.totals.visitors > 0 && <> ({fmtPct(playCr(play!.totals))})</>} in this period
          {play!.last_date ? <>, data up to {shortDay(play!.last_date)}</> : ', nothing imported for it yet'}.
          {' '}Play reports by its own day, lags 2–7 days, and only covers apps whose Play account has its
          reports bucket (GCS) set. Only the app and geo filters apply to these columns; — means no Play data.
        </div>
      )}
    </div>
  );
}
